
import { AIModule } from '../../types';

export const HEALTHCARE_AI_MODULE_2_MEDICAL_IMAGING: AIModule = {
  id: 'healthcare-ai-medical-imaging',
  title: 'AI in Medical Imaging',
  description: 'Deep learning applications for medical image analysis and diagnosis.',
  duration: '6 hours',
  difficulty: 'hard',
  content: `
# AI in Medical Imaging

Medical imaging is one of the most successful applications of AI in healthcare. This module covers advanced deep learning techniques for analyzing medical images across various modalities.

## Medical Imaging Modalities

### 1. X-ray Imaging
Traditional radiographic imaging for bone and chest analysis.

**AI Applications:**
- Chest X-ray analysis for pneumonia, COVID-19
- Bone fracture detection
- Cardiac silhouette analysis

### 2. Computed Tomography (CT)
Cross-sectional imaging using X-rays.

**AI Applications:**
- Cancer detection and staging
- Stroke diagnosis
- Trauma assessment

### 3. Magnetic Resonance Imaging (MRI)
Detailed soft tissue imaging using magnetic fields.

**AI Applications:**
- Brain tumor detection
- Multiple sclerosis monitoring
- Cardiac function analysis

### 4. Pathology
Microscopic tissue analysis.

**AI Applications:**
- Cancer diagnosis
- Tissue classification
- Cell counting and analysis

## Advanced CNN Architecture for Medical Imaging

\`\`\`python
import tensorflow as tf
from tensorflow.keras import layers, models, callbacks
import numpy as np
import cv2
from sklearn.metrics import classification_report, confusion_matrix
import matplotlib.pyplot as plt
import seaborn as sns

class MedicalCNN:
    def __init__(self, input_shape=(512, 512, 1), num_classes=3):
        self.input_shape = input_shape
        self.num_classes = num_classes
        self.model = self._build_advanced_cnn()
        
    def _build_advanced_cnn(self):
        """Build an advanced CNN architecture for medical imaging"""
        inputs = layers.Input(shape=self.input_shape)
        
        # Initial convolution
        x = layers.Conv2D(64, (7, 7), strides=2, padding='same')(inputs)
        x = layers.BatchNormalization()(x)
        x = layers.Activation('relu')(x)
        x = layers.MaxPooling2D((3, 3), strides=2, padding='same')(x)
        
        # Residual blocks
        x = self._residual_block(x, 64, stride=1)
        x = self._residual_block(x, 64, stride=1)
        x = self._residual_block(x, 128, stride=2)
        x = self._residual_block(x, 128, stride=1)
        x = self._residual_block(x, 256, stride=2)
        x = self._residual_block(x, 256, stride=1)
        x = self._residual_block(x, 512, stride=2)
        x = self._residual_block(x, 512, stride=1)
        
        # Attention mechanism
        x = self._attention_module(x)
        
        # Global average pooling
        x = layers.GlobalAveragePooling2D()(x)
        
        # Classification head
        x = layers.Dense(512, activation='relu')(x)
        x = layers.Dropout(0.5)(x)
        x = layers.Dense(256, activation='relu')(x)
        x = layers.Dropout(0.3)(x)
        outputs = layers.Dense(self.num_classes, activation='softmax')(x)
        
        model = models.Model(inputs, outputs)
        return model
    
    def _residual_block(self, x, filters, stride=1):
        """Residual block for deeper networks"""
        shortcut = x
        
        # First convolution
        x = layers.Conv2D(filters, (3, 3), strides=stride, padding='same')(x)
        x = layers.BatchNormalization()(x)
        x = layers.Activation('relu')(x)
        
        # Second convolution
        x = layers.Conv2D(filters, (3, 3), padding='same')(x)
        x = layers.BatchNormalization()(x)
        
        # Adjust shortcut if needed
        if stride != 1 or shortcut.shape[-1] != filters:
            shortcut = layers.Conv2D(filters, (1, 1), strides=stride, padding='same')(shortcut)
            shortcut = layers.BatchNormalization()(shortcut)
        
        # Add shortcut
        x = layers.Add()([x, shortcut])
        x = layers.Activation('relu')(x)
        
        return x
    
    def _attention_module(self, x):
        """Attention mechanism for focusing on relevant regions"""
        # Channel attention
        avg_pool = layers.GlobalAveragePooling2D(keepdims=True)(x)
        max_pool = layers.GlobalMaxPooling2D(keepdims=True)(x)
        
        # Shared MLP
        shared_layer_one = layers.Dense(x.shape[-1] // 8, activation='relu')
        shared_layer_two = layers.Dense(x.shape[-1])
        
        avg_pool = shared_layer_two(shared_layer_one(avg_pool))
        max_pool = shared_layer_two(shared_layer_one(max_pool))
        
        channel_attention = layers.Activation('sigmoid')(layers.Add()([avg_pool, max_pool]))
        
        # Apply channel attention
        x = layers.Multiply()([x, channel_attention])
        
        # Spatial attention
        avg_pool_spatial = tf.reduce_mean(x, axis=-1, keepdims=True)
        max_pool_spatial = tf.reduce_max(x, axis=-1, keepdims=True)
        spatial_attention = layers.Concatenate()([avg_pool_spatial, max_pool_spatial])
        spatial_attention = layers.Conv2D(1, (7, 7), padding='same', activation='sigmoid')(spatial_attention)
        
        # Apply spatial attention
        x = layers.Multiply()([x, spatial_attention])
        
        return x
    
    def compile_model(self, learning_rate=0.001):
        """Compile the model with appropriate loss and metrics"""
        self.model.compile(
            optimizer=tf.keras.optimizers.Adam(learning_rate=learning_rate),
            loss='categorical_crossentropy',
            metrics=['accuracy', 'precision', 'recall', 'f1_score']
        )
    
    def preprocess_medical_image(self, image_path, target_size=None):
        """Advanced preprocessing for medical images"""
        if target_size is None:
            target_size = self.input_shape[:2]
        
        # Load image
        if self.input_shape[-1] == 1:  # Grayscale
            image = cv2.imread(image_path, cv2.IMREAD_GRAYSCALE)
        else:
            image = cv2.imread(image_path)
            image = cv2.cvtColor(image, cv2.COLOR_BGR2RGB)
        
        # Resize
        image = cv2.resize(image, target_size)
        
        # CLAHE (Contrast Limited Adaptive Histogram Equalization)
        if len(image.shape) == 2:  # Grayscale
            clahe = cv2.createCLAHE(clipLimit=2.0, tileGridSize=(8, 8))
            image = clahe.apply(image)
        
        # Normalize
        image = image.astype(np.float32) / 255.0
        
        # Add channel dimension if grayscale
        if len(image.shape) == 2:
            image = np.expand_dims(image, axis=-1)
        
        return image
    
    def train_with_augmentation(self, train_data, val_data, epochs=100, 
                              use_mixed_precision=True):
        """Train model with data augmentation and mixed precision"""
        if use_mixed_precision:
            tf.keras.mixed_precision.set_global_policy('mixed_float16')
        
        # Data augmentation
        data_augmentation = tf.keras.Sequential([
            layers.RandomFlip("horizontal_and_vertical"),
            layers.RandomRotation(0.1),
            layers.RandomZoom(0.1),
            layers.RandomContrast(0.1),
            layers.RandomBrightness(0.1),
        ])
        
        # Callbacks
        callbacks_list = [
            callbacks.EarlyStopping(
                monitor='val_loss', patience=15, restore_best_weights=True
            ),
            callbacks.ReduceLROnPlateau(
                monitor='val_loss', factor=0.5, patience=8, min_lr=1e-7
            ),
            callbacks.ModelCheckpoint(
                'best_medical_model.h5', save_best_only=True, 
                monitor='val_accuracy', save_weights_only=False
            ),
            callbacks.CSVLogger('training_log.csv'),
            callbacks.TensorBoard(log_dir='./logs', histogram_freq=1)
        ]
        
        # Train model
        history = self.model.fit(
            train_data.map(lambda x, y: (data_augmentation(x, training=True), y)),
            epochs=epochs,
            validation_data=val_data,
            callbacks=callbacks_list,
            verbose=1
        )
        
        return history
    
    def predict_with_uncertainty(self, image, num_samples=50):
        """Predict with uncertainty estimation using Monte Carlo Dropout"""
        # Enable dropout during inference
        predictions = []
        
        for _ in range(num_samples):
            # Add dropout during inference for uncertainty estimation
            pred = self.model(image, training=True)
            predictions.append(pred.numpy())
        
        predictions = np.array(predictions)
        
        # Calculate mean and uncertainty
        mean_prediction = np.mean(predictions, axis=0)
        std_prediction = np.std(predictions, axis=0)
        
        return {
            'mean_prediction': mean_prediction,
            'uncertainty': std_prediction,
            'confidence_interval': {
                'lower': mean_prediction - 1.96 * std_prediction,
                'upper': mean_prediction + 1.96 * std_prediction
            }
        }
    
    def generate_grad_cam(self, image, class_index, layer_name=None):
        """Generate Grad-CAM visualization for model interpretability"""
        if layer_name is None:
            # Use the last convolutional layer
            for layer in reversed(self.model.layers):
                if isinstance(layer, layers.Conv2D):
                    layer_name = layer.name
                    break
        
        # Create a model that maps the input image to the activations
        grad_model = models.Model(
            [self.model.inputs], 
            [self.model.get_layer(layer_name).output, self.model.output]
        )
        
        # Compute the gradient of the class output with respect to feature map
        with tf.GradientTape() as tape:
            conv_outputs, predictions = grad_model(image)
            class_output = predictions[:, class_index]
        
        # Gradient of the class output with respect to the feature map
        grads = tape.gradient(class_output, conv_outputs)
        
        # Mean across the spatial dimensions
        pooled_grads = tf.reduce_mean(grads, axis=(0, 1, 2))
        
        # Weight feature map by gradients
        conv_outputs = conv_outputs[0]
        heatmap = conv_outputs @ pooled_grads[..., tf.newaxis]
        heatmap = tf.squeeze(heatmap)
        
        # Normalize heatmap
        heatmap = tf.maximum(heatmap, 0) / tf.math.reduce_max(heatmap)
        
        return heatmap.numpy()
    
    def clinical_evaluation(self, test_data, class_names):
        """Comprehensive clinical evaluation of the model"""
        # Get predictions
        predictions = self.model.predict(test_data)
        y_pred = np.argmax(predictions, axis=1)
        
        # Extract true labels
        y_true = []
        for batch in test_data:
            y_true.extend(np.argmax(batch[1], axis=1))
        y_true = np.array(y_true)
        
        # Classification report
        report = classification_report(y_true, y_pred, 
                                     target_names=class_names, 
                                     output_dict=True)
        
        # Confusion matrix
        cm = confusion_matrix(y_true, y_pred)
        
        # Calculate clinical metrics
        sensitivity = report['macro avg']['recall']  # True positive rate
        specificity = self._calculate_specificity(cm)
        
        # ROC AUC for each class
        roc_auc_scores = {}
        for i, class_name in enumerate(class_names):
            if len(class_names) == 2:  # Binary classification
                roc_auc_scores[class_name] = tf.keras.metrics.AUC()(
                    tf.one_hot(y_true, len(class_names))[:, i],
                    predictions[:, i]
                ).numpy()
        
        return {
            'classification_report': report,
            'confusion_matrix': cm,
            'sensitivity': sensitivity,
            'specificity': specificity,
            'roc_auc_scores': roc_auc_scores,
            'accuracy': report['accuracy']
        }
    
    def _calculate_specificity(self, confusion_matrix):
        """Calculate specificity from confusion matrix"""
        # For multiclass, calculate average specificity
        specificities = []
        for i in range(len(confusion_matrix)):
            tn = np.sum(confusion_matrix) - (
                np.sum(confusion_matrix[i, :]) + 
                np.sum(confusion_matrix[:, i]) - 
                confusion_matrix[i, i]
            )
            fp = np.sum(confusion_matrix[:, i]) - confusion_matrix[i, i]
            specificity = tn / (tn + fp) if (tn + fp) > 0 else 0
            specificities.append(specificity)
        
        return np.mean(specificities)

# Multi-modal Medical AI System
class MultiModalMedicalAI:
    def __init__(self):
        self.imaging_model = None
        self.clinical_model = None
        self.fusion_model = None
    
    def build_multimodal_model(self, image_shape, clinical_features):
        """Build a model that combines imaging and clinical data"""
        # Image processing branch
        image_input = layers.Input(shape=image_shape)
        image_features = self._build_image_branch(image_input)
        
        # Clinical data branch
        clinical_input = layers.Input(shape=(clinical_features,))
        clinical_features = self._build_clinical_branch(clinical_input)
        
        # Fusion layer
        combined = layers.Concatenate()([image_features, clinical_features])
        combined = layers.Dense(256, activation='relu')(combined)
        combined = layers.Dropout(0.5)(combined)
        combined = layers.Dense(128, activation='relu')(combined)
        combined = layers.Dropout(0.3)(combined)
        outputs = layers.Dense(2, activation='softmax')(combined)  # Binary classification
        
        self.fusion_model = models.Model([image_input, clinical_input], outputs)
        return self.fusion_model
    
    def _build_image_branch(self, inputs):
        """Build image processing branch"""
        x = layers.Conv2D(64, (3, 3), activation='relu')(inputs)
        x = layers.MaxPooling2D((2, 2))(x)
        x = layers.Conv2D(128, (3, 3), activation='relu')(x)
        x = layers.MaxPooling2D((2, 2))(x)
        x = layers.Conv2D(256, (3, 3), activation='relu')(x)
        x = layers.GlobalAveragePooling2D()(x)
        x = layers.Dense(128, activation='relu')(x)
        return x
    
    def _build_clinical_branch(self, inputs):
        """Build clinical data processing branch"""
        x = layers.Dense(64, activation='relu')(inputs)
        x = layers.Dropout(0.3)(x)
        x = layers.Dense(32, activation='relu')(x)
        return x

# Example usage:
# medical_cnn = MedicalCNN(input_shape=(512, 512, 1), num_classes=3)
# medical_cnn.compile_model()
# history = medical_cnn.train_with_augmentation(train_data, val_data)
# evaluation = medical_cnn.clinical_evaluation(test_data, ['Normal', 'Benign', 'Malignant'])
\`\`\`

## Model Validation and Clinical Testing

### Cross-validation Strategies
- K-fold cross-validation
- Stratified sampling
- Time-based validation
- Multi-site validation

### Clinical Performance Metrics
- Sensitivity (True Positive Rate)
- Specificity (True Negative Rate)
- Positive Predictive Value
- Negative Predictive Value
- Area Under the ROC Curve (AUC)

### Regulatory Considerations
- FDA 510(k) clearance process
- CE marking requirements
- Clinical trial design
- Real-world evidence studies

Medical imaging AI requires rigorous validation and careful consideration of clinical workflows to ensure safe and effective deployment in healthcare settings.
  `,
  quiz: {
    id: 'healthcare-ai-medical-imaging-quiz',
    title: 'Medical Imaging AI Quiz',
    passThreshold: 75,
    questions: [
      {
        id: 'q1',
        question: 'What is Grad-CAM used for in medical imaging AI?',
        type: 'multiple-choice',
        options: [
          'To improve model accuracy',
          'To visualize which parts of an image the model focuses on',
          'To reduce training time',
          'To compress medical images'
        ],
        correctAnswer: 1,
        explanation: 'Grad-CAM (Gradient-weighted Class Activation Mapping) creates visual explanations for CNN-based models by highlighting the important regions in the input image.',
        difficulty: 'hard',
        points: 20
      }
    ]
  },
  resources: [
    {
      id: 'medical-imaging-video',
      title: 'AI in Medical Imaging',
      type: 'video',
      url: 'https://example.com/medical-imaging',
      description: 'Advanced techniques for medical image analysis'
    }
  ]
};
